import { useEffect, useMemo, useRef, useState } from "react";
import "./CriarEmail.css";

function formatPhoneBR(value = "") {
  const d = String(value).replace(/\D/g, "").slice(0, 11);
  const p1 = d.slice(0, 2);
  const p2 = d.slice(2, 3);
  const p3 = d.slice(3, 7);
  const p4 = d.slice(7, 11);

  let out = p1;
  if (p2) out += (out ? " " : "") + p2;
  if (p3) out += " " + p3;
  if (p4) out += " - " + p4;
  return out;
}

const GROUP_OPTIONS = [
  "CCO",
  "PPCO",
  "UP Centro",
  "UP Norte",
  "UP Centro Norte",
  "UP Portos",
  "UP RS",
  "UP Serras",
  "Gerências",
  "Teste",
];

const ATIVO_OPTIONS = [
  { value: 1, label: "SIM" },
  { value: 0, label: "NÃO" },
];

function orderGroups(groups = []) {
  return [...groups].sort((a, b) => {
    const ai = GROUP_OPTIONS.indexOf(a);
    const bi = GROUP_OPTIONS.indexOf(b);
    const av = ai === -1 ? Number.MAX_SAFE_INTEGER : ai;
    const bv = bi === -1 ? Number.MAX_SAFE_INTEGER : bi;
    return av - bv;
  });
}

function CriarEmailDropdown({ label, name, value, options, onSelect, placeholder = "SELECIONE" }) {
  const [open, setOpen] = useState(false);
  const [renderMenu, setRenderMenu] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [menuMaxHeight, setMenuMaxHeight] = useState(220);
  const wrapRef = useRef(null);
  const closeTimerRef = useRef(null);
  const itemHeight = 44;

  const current = useMemo(
    () => options.find((o) => String(o.value) === String(value)) || null,
    [options, value]
  );

  useEffect(() => {
    function onDocDown(e) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) closeMenu();
    }
    document.addEventListener("mousedown", onDocDown);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!open) return;

    const onWindowChange = () => updatePlacement();
    window.addEventListener("resize", onWindowChange);
    window.addEventListener("scroll", onWindowChange, true);
    return () => {
      window.removeEventListener("resize", onWindowChange);
      window.removeEventListener("scroll", onWindowChange, true);
    };
  }, [open, options.length]);

  function updatePlacement() {
    if (!wrapRef.current) return;

    const rect = wrapRef.current.getBoundingClientRect();
    const modal = wrapRef.current.closest(".emcr-box");
    const modalRect = modal?.getBoundingClientRect();

    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    const safePad = 12;

    const viewportBelow = Math.max(0, viewportH - rect.bottom - safePad);
    const viewportAbove = Math.max(0, rect.top - safePad);
    const modalBelow = modalRect ? Math.max(0, modalRect.bottom - rect.bottom - safePad) : Number.POSITIVE_INFINITY;
    const modalAbove = modalRect ? Math.max(0, rect.top - modalRect.top - safePad) : Number.POSITIVE_INFINITY;

    const spaceBelow = Math.max(0, Math.min(viewportBelow, modalBelow));
    const spaceAbove = Math.max(0, Math.min(viewportAbove, modalAbove));

    const target = Math.min(320, options.length * itemHeight + 12);
    const shouldOpenUp = spaceBelow < 190 && spaceAbove > spaceBelow;
    const available = shouldOpenUp ? spaceAbove : spaceBelow;
    const dynamicMax = Math.max(96, Math.min(target, available - 6));

    setOpenUp(shouldOpenUp);
    setMenuMaxHeight(dynamicMax);
  }

  const openMenu = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setRenderMenu(true);
    requestAnimationFrame(() => {
      updatePlacement();
      setOpen(true);
    });
  };

  const closeMenu = () => {
    setOpen(false);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setRenderMenu(false), 240);
  };

  return (
    <div
      ref={wrapRef}
      className={`field emcr-field-select ${open ? "is-open" : ""} ${openUp ? "is-open-up" : ""}`}
    >
      <label>{label}</label>
      <div className="emcr-pick-anchor">
        <button type="button" className="emcr-pick-btn" aria-haspopup="listbox" aria-expanded={open} onClick={() => (open ? closeMenu() : openMenu())}>
          <span className={`emcr-pick-val ${current ? "" : "is-placeholder"}`}>
            {current ? current.label : placeholder}
          </span>
        </button>

        {renderMenu && (
          <div
            className={`emcr-pick-menu ${open ? "is-open" : "is-closing"}`}
            role="listbox"
            aria-label={label}
            style={{ maxHeight: `${menuMaxHeight}px` }}
          >
            {options.map((opt) => (
              <button
                key={String(opt.value)}
                type="button"
                className="emcr-pick-opt"
                onClick={() => {
                  onSelect(name, opt.value);
                  closeMenu();
                }}
                role="option"
                aria-selected={String(opt.value) === String(value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function CriarEmail({ open, onClose, onConfirm }) {
  const [form, setForm] = useState({
    nome: "",
    apelido: "",
    grupos: [],
    email: "",
    telefone: "",
    observacao: "",
    ativo: "",
  });

  function toggleGrupo(grupo) {
    setForm((s) => {
      const current = Array.isArray(s.grupos) ? s.grupos : [];
      const exists = current.includes(grupo);
      const next = exists ? current.filter((g) => g !== grupo) : [...current, grupo];
      return { ...s, grupos: orderGroups(next) };
    });
  }

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setForm({
      nome: "",
      apelido: "",
      grupos: [],
      email: "",
      telefone: "",
      observacao: "",
      ativo: "",
    });
  }, [open]);

  if (!open) return null;

  const canSave = !!String(form.email || "").trim() && (Number(form.ativo) === 0 || Number(form.ativo) === 1);

  return (
    <div className="emcr-bg" data-emcr>
      <div className="emcr-box" role="dialog" aria-modal="true" aria-label="Cadastrar e-mail">
        <header className="emcr-head">
          <h3 className="emcr-title">Cadastrar e-mail</h3>
          <button className="emcr-x" type="button" onClick={onClose} aria-label="Fechar">
            x
          </button>
        </header>

        <div className="emcr-body">
          <div className="row">
            <div className="field">
              <label>E-mail *</label>
              <input
                value={form.email}
                onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
                placeholder="nome@empresa.com.br"
              />
            </div>
            <div className="field">
              <label>Nome</label>
              <input
                value={form.nome}
                onChange={(e) => setForm((s) => ({ ...s, nome: e.target.value }))}
                placeholder="Nome completo"
              />
            </div>
          </div>

          <div className="row">
            <div className="field">
              <label>Apelido</label>
              <input
                value={form.apelido}
                onChange={(e) => setForm((s) => ({ ...s, apelido: e.target.value }))}
                placeholder="Como será exibido"
              />
            </div>

            <CriarEmailDropdown
              label="Ativo *"
              name="ativo"
              value={form.ativo}
              options={ATIVO_OPTIONS}
              onSelect={(name, nextValue) => setForm((s) => ({ ...s, [name]: Number(nextValue) }))}
              placeholder="SELECIONE"
            />

            <div className="field">
              <label>Telefone</label>
              <input
                value={form.telefone}
                onChange={(e) => setForm((s) => ({ ...s, telefone: formatPhoneBR(e.target.value) }))}
                placeholder="41 9 9999 - 9999"
                inputMode="numeric"
                maxLength={16}
              />
            </div>
          </div>

          <div className="row">
            <div className="field grow">
              <label>Grupos (clique para marcar/desmarcar)</label>
              <div className="emcr-groups-grid">
                {GROUP_OPTIONS.map((grupo) => {
                  const selected = form.grupos.includes(grupo);
                  return (
                    <button
                      key={grupo}
                      type="button"
                      className={`emcr-group-chip ${selected ? "is-on" : ""}`}
                      onClick={() => toggleGrupo(grupo)}
                    >
                      {grupo}
                    </button>
                  );
                })}
              </div>
              <small>{form.grupos.length} grupo(s) selecionado(s)</small>
            </div>
          </div>

          <div className="row">
            <div className="field grow">
              <label>Observação</label>
              <textarea
                rows={3}
                value={form.observacao}
                onChange={(e) => setForm((s) => ({ ...s, observacao: e.target.value }))}
                placeholder="Notas, área, turno, etc."
              />
            </div>
          </div>
        </div>

        <footer className="emcr-foot">
          <button className="emcr-btn emcr-btn--ghost" type="button" onClick={onClose}>
            Cancelar
          </button>
          <button
            className="emcr-btn emcr-btn--primary"
            type="button"
            onClick={() =>
              onConfirm({
                ...form,
                grupo: form.grupos[0] || "",
                grupos: form.grupos,
              })
            }
            disabled={!canSave}
          >
            Salvar
          </button>
        </footer>
      </div>
    </div>
  );
}
